import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

// Brand colour
const BRAND    = rgb(0 / 255, 120 / 255, 191 / 255); // #0078BF
const BLACK    = rgb(0.1,  0.1,  0.1);
const MUTED    = rgb(0.45, 0.45, 0.45);
const LIGHT    = rgb(0.93, 0.96, 0.99);  // header-row fill
const WHITE    = rgb(1,    1,    1);
const BORDER   = rgb(0.82, 0.87, 0.92);

const money = (n) =>
  'Rs.' + Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const lineTotal = (p) => {
  const base = (Number(p.qty) || 0) * (Number(p.rate) || 0);
  return base + base * (Number(p.gstPercent) || 0) / 100;
};

/**
 * Generates a branded quotation PDF.
 *
 * @param {object} quotation  – CRM quotation record
 * @param {string} label      – e.g. "Q1" or "Q2.1"
 * @returns {Promise<Uint8Array>} raw PDF bytes
 */
export async function generateQuotationPDF(quotation, label) {
  const pdfDoc = await PDFDocument.create();
  const helvetica      = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold  = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const PAGE_W = 595;   // A4 portrait points
  const PAGE_H = 842;
  const ML = 44;        // margin left
  const MR = 44;        // margin right
  const CONTENT_W = PAGE_W - ML - MR;

  const products = Array.isArray(quotation.products) ? quotation.products : [];

  // ── estimate total height needed ─────────────────────────────────────
  // Header 110 + billed-to ~80 + table header 22 + rows ~40 each + footer ~60
  const estimatedH = 110 + 80 + 22 + products.length * 44 + 80;
  const totalH = Math.max(PAGE_H, estimatedH + 60);

  const page = pdfDoc.addPage([PAGE_W, totalH]);
  const draw = (opts) => page.drawText(opts.text, {
    x: opts.x, y: opts.y,
    size: opts.size || 10,
    font: opts.bold ? helveticaBold : helvetica,
    color: opts.color || BLACK,
    maxWidth: opts.maxWidth,
    lineHeight: opts.lineHeight || (opts.size || 10) * 1.4,
  });

  let y = totalH - 44; // cursor starts near top

  // ── 1. Header bar ────────────────────────────────────────────────────
  page.drawRectangle({ x: 0, y: totalH - 80, width: PAGE_W, height: 80, color: BRAND });

  draw({ text: 'Angel Printers', x: ML, y: y - 4, size: 20, bold: true, color: WHITE });
  draw({ text: 'Quotation ' + label, x: ML, y: y - 24, size: 11, color: rgb(0.8, 0.93, 1) });

  // Right-aligned: date
  const dateStr = new Date(quotation.createdAt || Date.now())
    .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const dateW = helvetica.widthOfTextAtSize(dateStr, 10);
  draw({ text: dateStr, x: PAGE_W - MR - dateW, y: y - 4, size: 10, color: rgb(0.8, 0.93, 1) });

  y = totalH - 80 - 22;

  // ── 2. Billed To block ───────────────────────────────────────────────
  draw({ text: 'BILLED TO', x: ML, y, size: 8, color: BRAND, bold: true });
  y -= 14;
  draw({ text: quotation.customerName || '', x: ML, y, size: 12, bold: true });
  y -= 16;

  if (quotation.companyName) {
    draw({ text: quotation.companyName, x: ML, y, size: 10, color: MUTED });
    y -= 14;
  }
  if (quotation.gstNo) {
    draw({ text: 'GSTIN: ' + quotation.gstNo, x: ML, y, size: 9, color: MUTED });
    y -= 13;
  }
  if (quotation.companyAddress) {
    draw({ text: quotation.companyAddress, x: ML, y, size: 9, color: MUTED, maxWidth: CONTENT_W * 0.6 });
    y -= 13;
  }
  draw({ text: quotation.customerPhone || '', x: ML, y, size: 9, color: MUTED });
  y -= 13;
  if (quotation.customerEmail) {
    draw({ text: quotation.customerEmail, x: ML, y, size: 9, color: MUTED });
    y -= 13;
  }

  y -= 10;

  // ── 3. Divider ───────────────────────────────────────────────────────
  page.drawLine({ start: { x: ML, y }, end: { x: PAGE_W - MR, y }, thickness: 0.5, color: BORDER });
  y -= 16;

  // ── 4. Products table ────────────────────────────────────────────────
  // Column X positions & widths
  const COL = {
    product: { x: ML,            w: CONTENT_W * 0.38 },
    desc:    { x: ML + CONTENT_W * 0.38, w: CONTENT_W * 0.20 },
    qty:     { x: ML + CONTENT_W * 0.58, w: CONTENT_W * 0.08 },
    rate:    { x: ML + CONTENT_W * 0.66, w: CONTENT_W * 0.14 },
    gst:     { x: ML + CONTENT_W * 0.80, w: CONTENT_W * 0.08 },
    total:   { x: ML + CONTENT_W * 0.88, w: CONTENT_W * 0.12 },
  };

  const ROW_H = 22;

  // Header row bg
  page.drawRectangle({ x: ML, y: y - ROW_H + 6, width: CONTENT_W, height: ROW_H, color: LIGHT });

  const headers = [
    ['Product',     COL.product],
    ['Description', COL.desc],
    ['Qty',         COL.qty],
    ['Rate',        COL.rate],
    ['GST',         COL.gst],
    ['Total',       COL.total],
  ];
  headers.forEach(([label, col]) => {
    draw({ text: label, x: col.x, y: y - 10, size: 8, bold: true, color: BRAND });
  });
  y -= ROW_H;

  // Data rows
  products.forEach((p, idx) => {
    const rowBg = idx % 2 === 1 ? rgb(0.97, 0.98, 1) : WHITE;
    const rowTotal = lineTotal(p);

    // Estimate row height (product name may wrap)
    const nameLines = Math.ceil(helveticaBold.widthOfTextAtSize(p.name || '', 9) / (COL.product.w - 4)) || 1;
    const descLines = p.description
      ? Math.ceil(helvetica.widthOfTextAtSize(p.description, 8) / (COL.desc.w - 4)) || 1
      : 0;
    const contentLines = Math.max(nameLines, descLines, 1);
    const rH = Math.max(ROW_H, contentLines * 13 + 10);

    page.drawRectangle({ x: ML, y: y - rH + 6, width: CONTENT_W, height: rH, color: rowBg });

    const textY = y - 10;
    draw({ text: p.name || '', x: COL.product.x, y: textY, size: 9, bold: true, maxWidth: COL.product.w - 4 });
    if (p.description) {
      draw({ text: p.description, x: COL.desc.x, y: textY, size: 8, color: MUTED, maxWidth: COL.desc.w - 4 });
    }
    draw({ text: String(p.qty || 1),              x: COL.qty.x,   y: textY, size: 9 });
    draw({ text: money(p.rate),                   x: COL.rate.x,  y: textY, size: 9 });
    draw({ text: (p.gstPercent || 0) + '%',       x: COL.gst.x,   y: textY, size: 9 });
    draw({ text: money(rowTotal),                 x: COL.total.x, y: textY, size: 9, bold: true });

    y -= rH;
  });

  // Bottom border of table
  page.drawLine({ start: { x: ML, y: y + 4 }, end: { x: PAGE_W - MR, y: y + 4 }, thickness: 0.5, color: BORDER });
  y -= 8;

  // ── 5. Grand Total bar ───────────────────────────────────────────────
  page.drawRectangle({ x: ML, y: y - 28, width: CONTENT_W, height: 32, color: BRAND });
  draw({ text: 'GRAND TOTAL', x: ML + 8, y: y - 18, size: 10, bold: true, color: WHITE });
  const gtStr = money(quotation.grandTotal || 0);
  const gtW = helveticaBold.widthOfTextAtSize(gtStr, 13);
  draw({ text: gtStr, x: PAGE_W - MR - gtW, y: y - 20, size: 13, bold: true, color: WHITE });
  y -= 40;

  // ── 6. Footer ────────────────────────────────────────────────────────
  y -= 16;
  draw({ text: 'Thank you for choosing Angel Printers!', x: ML, y, size: 9, color: MUTED });
  y -= 13;
  draw({ text: 'This is a computer-generated quotation.', x: ML, y, size: 8, color: rgb(0.65, 0.65, 0.65) });

  // Footer brand line at very bottom
  page.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: 24, color: BRAND });
  draw({ text: 'Angel Printers', x: ML, y: 7, size: 9, bold: true, color: WHITE });
  const footerRight = 'Quotation ' + label;
  const frW = helvetica.widthOfTextAtSize(footerRight, 8);
  draw({ text: footerRight, x: PAGE_W - MR - frW, y: 8, size: 8, color: rgb(0.8, 0.93, 1) });

  return pdfDoc.save();
}

/**
 * Triggers browser download of the PDF.
 */
export function downloadQuotationPDF(pdfBytes, label) {
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Quotation-${label}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
